import { Shuffle, Type, Eye, Monitor, Download, Copy, Check, Maximize2 } from 'lucide-react';
import { spacing } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { aspectRatios } from '../../constants/aspectRatios';
import { asciiStyles } from '../../constants/asciiStyles';
import RangeSlider from '../ui/RangeSlider';
import DualColorPicker from '../ui/ColorPicker';
import Listbox from '../ui/Listbox';
import CollapsibleSection from '../ui/CollapsibleSection';

/**
 * RightSidebar Component
 *
 * Right sidebar containing all ASCII rendering controls and export options.
 * Includes sliders for all settings, format selection, and export controls.
 * Controls are disabled in creative mode until a pattern is generated.
 */
const RightSidebar = ({
  settings,
  mode,
  hasPattern = true,
  onRandomize,
  isRandomizing,
  hasUploadedMedia,
  onDensityChange,
  onColumnsChange,
  onAspectRatioChange,
  onGammaChange,
  onContrastChange,
  onCharacterCountChange,
  onAsciiStyleChange,
  onCharacterSetChange,
  onGifBackgroundColorChange,
  onGifForegroundColorChange,
  onDimensionModeChange,
  onCustomWidthChange,
  onCustomHeightChange,
  // Export controls
  exportFormat,
  onExportFormatChange,
  onFullscreen,
  summary,
  summaryPulse,
  onDownload,
  onCopy,
  isDownloading,
  downloaded,
  copied,
  gifProgress = 0,
  isGeneratingGif = false,
  gifComplete = false
}) => {
  const { colors } = useTheme();

  const {
    density,
    columns,
    aspectRatio,
    gamma,
    contrast,
    characterCount,
    asciiStyle,
    characterSet,
    gifBackgroundColor,
    gifForegroundColor,
    dimensionMode,
    customWidth,
    customHeight
  } = settings;

  const styleOptions = Object.values(asciiStyles);

  // Disable controls when in creative mode without a pattern
  const controlsDisabled = mode === 'creative' && !hasPattern;

  // Determine if we're in any downloading state
  const isAnyDownloading = isDownloading || isGeneratingGif;
  const isAnyComplete = downloaded || gifComplete;

  return (
    <div style={{
      width: '280px',
      minWidth: '280px',
      maxWidth: '280px',
      flexShrink: 0,
      background: colors.background,
      padding: spacing.xxl,
      overflowY: controlsDisabled ? 'hidden' : 'auto',
      borderLeft: `1px solid ${colors.border}`,
      position: 'relative'
    }}>
      {/* Disabled overlay */}
      {controlsDisabled && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: colors.background,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xxl,
          pointerEvents: 'auto'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '180px'
          }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: colors.backgroundOffset,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              marginBottom: spacing.md
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.foregroundSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div style={{
              color: colors.foreground,
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.5,
              marginBottom: spacing.xs
            }}>
              Controls locked
            </div>
            <div style={{
              color: colors.foregroundSecondary,
              fontSize: '0.75rem',
              lineHeight: 1.4
            }}>
              Generate a pattern first to adjust settings
            </div>
          </div>
        </div>
      )}

      {/* Export Section */}
      {!controlsDisabled && (
        <div style={{
          marginBottom: spacing.xxl,
          paddingBottom: spacing.xxl,
          borderBottom: `1px solid ${colors.border}`
        }}>
          <h3 style={{
            margin: 0,
            marginBottom: spacing.lg,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: colors.foreground
          }}>
            Export
          </h3>

          {/* Format & Preview */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 500,
              marginBottom: spacing.xs,
              color: colors.foreground
            }}>
              Format
            </label>

            <div style={{
              display: 'flex',
              gap: '6px'
            }}>
              {/* PNG/GIF Toggle */}
              <div style={{
                flex: 1,
                display: 'inline-flex',
                gap: '3px',
                padding: '3px',
                background: colors.backgroundOffset,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`
              }}>
                {['PNG', 'GIF'].map((format) => (
                  <button
                    key={format}
                    onClick={() => onExportFormatChange(format.toLowerCase())}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      color: exportFormat === format.toLowerCase() ? colors.background : colors.foregroundSecondary,
                      background: exportFormat === format.toLowerCase() ? colors.foreground : 'transparent',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      outline: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (exportFormat !== format.toLowerCase()) {
                        e.currentTarget.style.color = colors.foreground;
                      } else {
                        e.currentTarget.style.opacity = '0.85';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (exportFormat !== format.toLowerCase()) {
                        e.currentTarget.style.color = colors.foregroundSecondary;
                      } else {
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                  >
                    {format}
                  </button>
                ))}
              </div>

              {/* Fullscreen Button */}
              <button
                onClick={onFullscreen}
                style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.foregroundSecondary,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.foregroundTertiary;
                  e.currentTarget.style.color = colors.foreground;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.color = colors.foregroundSecondary;
                }}
                title="View fullscreen"
              >
                <Maximize2 size={14} />
              </button>
            </div>

            {/* Summary */}
            <div style={{
              fontSize: '0.6875rem',
              color: colors.foregroundSecondary,
              marginTop: spacing.xs,
              transform: summaryPulse ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}>
              {summary}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onDownload}
              disabled={isAnyDownloading}
              style={{
                flex: 1,
                height: '36px',
                padding: '0 12px',
                fontSize: '0.8125rem',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: colors.background,
                background: colors.foreground,
                border: 'none',
                borderRadius: '6px',
                cursor: isAnyDownloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                opacity: isAnyDownloading ? 0.6 : 1,
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!isAnyDownloading) {
                  e.currentTarget.style.opacity = '0.85';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isAnyDownloading) {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {isAnyDownloading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: isGeneratingGif ? `${gifProgress}%` : '100%',
                  height: '100%',
                  background: 'rgba(0,0,0,0.15)',
                  transition: isGeneratingGif ? 'width 0.3s ease-out' : 'none'
                }} />
              )}
              <style>{`
                @keyframes bounce {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-2px); }
                }
              `}</style>
              {isAnyComplete ? (
                <Check size={14} />
              ) : isAnyDownloading ? (
                <Download size={14} style={{animation: 'bounce 0.6s infinite'}} />
              ) : (
                <Download size={14} />
              )}
              {isAnyComplete ? 'Done!' : isAnyDownloading ? (isGeneratingGif ? `${Math.round(gifProgress)}%` : '...') : exportFormat.toUpperCase()}
            </button>

            <button
              onClick={onCopy}
              style={{
                flex: 1,
                height: '36px',
                padding: '0 12px',
                fontSize: '0.8125rem',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: colors.foreground,
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.foregroundTertiary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              {copied && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)',
                  animation: 'shimmer 0.8s ease-in-out'
                }} />
              )}
              <style>{`
                @keyframes shimmer {
                  0% { left: -100%; }
                  100% { left: 100%; }
                }
              `}</style>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Done!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.xxl
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: colors.foreground
        }}>
          Controls
        </h3>

        <button
          onClick={onRandomize}
          disabled={isRandomizing || mode !== 'preset' || controlsDisabled}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            background: 'transparent',
            cursor: mode !== 'preset' || controlsDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.foregroundSecondary,
            transition: 'all 0.15s ease',
            opacity: mode !== 'preset' || controlsDisabled ? 0.4 : 1,
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (mode === 'preset' && !isRandomizing && !controlsDisabled) {
              e.currentTarget.style.borderColor = colors.foregroundTertiary;
              e.currentTarget.style.color = colors.foreground;
              e.currentTarget.style.transform = 'rotate(90deg)';
            }
          }}
          onMouseLeave={(e) => {
            if (mode === 'preset' && !controlsDisabled) {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.color = colors.foregroundSecondary;
              e.currentTarget.style.transform = 'rotate(0deg)';
            }
          }}
          title="Randomize all settings"
        >
          <Shuffle size={14} />
        </button>
      </div>

      {/* Characters Section */}
      <CollapsibleSection title="Characters" icon={Type}>
        {/* ASCII Style */}
        <div style={{ marginBottom: spacing.lg }}>
          <Listbox
            label="Style"
            value={asciiStyle}
            onChange={onAsciiStyleChange}
            options={styleOptions}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Choose predefined character sets
          </div>
        </div>

        {/* Character Variety */}
        <div style={{ marginBottom: spacing.lg }}>
          <RangeSlider
            min={2}
            max={characterSet.length}
            step={1}
            value={Math.min(characterCount, characterSet.length)}
            onChange={onCharacterCountChange}
            label={`Count: ${characterCount} / ${characterSet.length}`}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Number of unique characters used
          </div>
        </div>

        {/* Character Set */}
        {!controlsDisabled && (
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 500,
              marginBottom: spacing.sm,
              color: colors.foreground
            }}>
              Character Set
            </label>
            <input
              type="text"
              value={characterSet}
              onChange={onCharacterSetChange}
              placeholder="Enter characters..."
              style={{
                width: '100%',
                height: '40px',
                padding: '0 16px',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '1px',
                color: colors.foreground,
                background: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.15s ease',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.foreground;
                e.target.style.boxShadow = `0 0 0 4px ${colors.backgroundOffset}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
              }}
              onMouseEnter={(e) => {
                if (document.activeElement !== e.target) {
                  e.target.style.borderColor = colors.foregroundTertiary;
                }
              }}
              onMouseLeave={(e) => {
                if (document.activeElement !== e.target) {
                  e.target.style.borderColor = colors.border;
                }
              }}
            />
            <div style={{
              fontSize: '0.6875rem',
              color: colors.foregroundSecondary,
              marginTop: spacing.sm
            }}>
              Auto-sorted by density
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Visual Section */}
      <CollapsibleSection title="Visual" icon={Eye}>
        {/* Presence Control */}
        <div style={{ marginBottom: spacing.lg }}>
          <RangeSlider
            min={0.3}
            max={1.5}
            step={0.1}
            value={density}
            onChange={onDensityChange}
            label={`Presence: ${density.toFixed(1)}`}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Adjust character density
          </div>
        </div>

        {/* Tonality */}
        <div style={{ marginBottom: spacing.lg }}>
          <RangeSlider
            min={0.5}
            max={2.0}
            step={0.1}
            value={gamma}
            onChange={onGammaChange}
            label={`Tonality: ${gamma.toFixed(1)}`}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Control brightness and midtones
          </div>
        </div>

        {/* Intensity */}
        <div style={{ marginBottom: spacing.lg }}>
          <RangeSlider
            min={0.5}
            max={2.0}
            step={0.1}
            value={contrast}
            onChange={onContrastChange}
            label={`Intensity: ${contrast.toFixed(1)}`}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Adjust contrast between light and dark
          </div>
        </div>

        {/* Colors */}
        {!controlsDisabled && (
          <div>
            <DualColorPicker
              key={mode}
              label="Colors"
              backgroundColor={gifBackgroundColor}
              foregroundColor={gifForegroundColor}
              onBackgroundColorChange={onGifBackgroundColorChange}
              onForegroundColorChange={onGifForegroundColorChange}
            />
            <div style={{
              fontSize: '0.6875rem',
              color: colors.foregroundSecondary,
              marginTop: spacing.sm
            }}>
              Export background and foreground colors
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Export Settings Section */}
      <CollapsibleSection title="Export Settings" icon={Monitor}>
        {/* Resolution */}
        <div style={{ marginBottom: spacing.lg }}>
          <RangeSlider
            min={40}
            max={200}
            step={10}
            value={columns}
            onChange={onColumnsChange}
            label={`Resolution: ${columns}`}
            disabled={controlsDisabled}
          />
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginTop: spacing.xs
          }}>
            Character columns (higher = more detail)
          </div>
        </div>

        {/* Dimensions */}
        {!controlsDisabled && (
          <div style={{ marginBottom: spacing.lg }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: spacing.xs,
            color: colors.foreground
          }}>
            Dimensions
          </label>
          <div style={{
            fontSize: '0.6875rem',
            color: colors.foregroundSecondary,
            marginBottom: spacing.md
          }}>
            Set export size by ratio or exact pixels
          </div>

          {/* Mode Toggle */}
          <div style={{
            display: 'inline-flex',
            gap: '3px',
            padding: '3px',
            background: colors.backgroundOffset,
            borderRadius: '8px',
            marginBottom: spacing.md,
            border: `1px solid ${colors.border}`
          }}>
            {['Aspect Ratio', 'Custom'].map((mode) => {
              const modeValue = mode === 'Aspect Ratio' ? 'aspectRatio' : 'custom';
              return (
                <button
                  key={mode}
                  onClick={() => onDimensionModeChange(modeValue)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '-0.005em',
                    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    color: dimensionMode === modeValue ? colors.background : colors.foregroundSecondary,
                    background: dimensionMode === modeValue ? colors.foreground : 'transparent',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (dimensionMode !== modeValue) {
                      e.currentTarget.style.color = colors.foreground;
                    } else {
                      e.currentTarget.style.opacity = '0.85';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (dimensionMode !== modeValue) {
                      e.currentTarget.style.color = colors.foregroundSecondary;
                    } else {
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  {mode}
                </button>
              );
            })}
          </div>

          {/* Conditional Content */}
          {dimensionMode === 'aspectRatio' ? (
            // Aspect Ratio Mode
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px'
            }}>
              {Object.entries(aspectRatios).map(([key, { label }]) => {
                const isSelected = aspectRatio === key;
                return (
                  <button
                    key={key}
                    onClick={() => onAspectRatioChange(key)}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = colors.foregroundTertiary;
                      } else {
                        e.currentTarget.style.opacity = '0.85';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = colors.border;
                      } else {
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    style={{
                      height: '36px',
                      padding: '0 12px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      color: isSelected ? colors.background : colors.foreground,
                      background: isSelected ? colors.foreground : 'transparent',
                      border: `1px solid ${isSelected ? colors.foreground : colors.border}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      outline: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : (
            // Custom Pixels Mode
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Width Input */}
              <div style={{ position: 'relative', flex: 1 }}>
                <label style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: colors.foregroundSecondary,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}>
                  W
                </label>
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => onCustomWidthChange(parseInt(e.target.value) || 1920)}
                  min={320}
                  max={3840}
                  step={1}
                  style={{
                    width: '100%',
                    height: '40px',
                    paddingLeft: '28px',
                    paddingRight: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                    color: colors.foreground,
                    background: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    boxSizing: 'border-box',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.foreground;
                    e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.backgroundOffset}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.target) {
                      e.target.style.borderColor = colors.foregroundTertiary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.target) {
                      e.target.style.borderColor = colors.border;
                    }
                  }}
                />
              </div>

              {/* Height Input */}
              <div style={{ position: 'relative', flex: 1 }}>
                <label style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: colors.foregroundSecondary,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}>
                  H
                </label>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => onCustomHeightChange(parseInt(e.target.value) || 1080)}
                  min={180}
                  max={2160}
                  step={1}
                  style={{
                    width: '100%',
                    height: '40px',
                    paddingLeft: '28px',
                    paddingRight: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                    color: colors.foreground,
                    background: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    boxSizing: 'border-box',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.foreground;
                    e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.backgroundOffset}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.target) {
                      e.target.style.borderColor = colors.foregroundTertiary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.target) {
                      e.target.style.borderColor = colors.border;
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      </CollapsibleSection>
    </div>
  );
};

export default RightSidebar;
